import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Input, Spinner } from '@easydent/shared-ui';
import {
  getMe, getExports, requestExport, downloadExport, getClosure, requestClosure, cancelClosure,
  type SupplierExport, type SupplierClosureState,
} from '@/lib/api';

/**
 * Your data, and your way out.
 *
 * Two things live here, and they are kept visually and structurally apart on purpose: exporting is
 * routine and reversible, closing is neither. A combined "download and delete" control would make
 * the destructive act a side effect of the harmless one.
 *
 * ⚠️ The page states plainly what a closure does NOT delete — impressions, wallet history and
 * invoices all survive it. Saying "your data will be deleted" and then keeping records would be the
 * kind of true-in-the-small, false-in-the-large claim that destroys trust the moment somebody
 * notices. They survive for defensible reasons (they are the evidence behind what we charged, and
 * our own accounting is under fiscal retention), and the reasons are worth giving.
 */

const dateFmt = (iso: string | null, lang: string) =>
  iso ? new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'long', year: 'numeric' }) : '';

const statusTone: Record<string, string> = {
  Ready: 'bg-brand-50 text-brand-ink-strong',
  Pending: 'bg-amber-50 text-amber-700',
  Running: 'bg-amber-50 text-amber-700',
  Failed: 'bg-red-50 text-red-600',
};

const AccountPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [exports, setExports] = useState<SupplierExport[] | null>(null);
  const [closure, setClosure] = useState<SupplierClosureState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [showClose, setShowClose] = useState(false);

  const load = async () => {
    try {
      const [me, rows, state] = await Promise.all([getMe(), getExports(), getClosure()]);
      setCompanyName(me.name);
      setExports(rows);
      setClosure(state);
    } catch {
      setError(t('account.loadError', 'Could not load your account details.'));
    }
  };
  useEffect(() => { load(); }, []);

  // An export is built by a background job, so the page has to keep looking while one is in flight.
  // It stops as soon as nothing is pending — an interval that never clears is a battery cost on a
  // tab somebody left open.
  useEffect(() => {
    const working = exports?.some((e) => e.status === 'Pending' || e.status === 'Running');
    if (!working) return;
    const id = setInterval(() => { getExports().then(setExports).catch(() => {}); }, 10_000);
    return () => clearInterval(id);
  }, [exports]);

  const onExport = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await requestExport();
      setExports(await getExports());
      setNotice(t('account.exportQueued', 'Your export is being prepared. It usually takes a few minutes — you can leave this page.'));
    } catch {
      setError(t('account.exportError', 'Could not start the export. Please try again.'));
    } finally { setBusy(false); }
  };

  const onDownload = async (id: string) => {
    setError(null);
    try { await downloadExport(id); }
    catch { setError(t('account.downloadError', 'That download link could not be created. Try again.')); }
  };

  const onClose = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      setClosure(await requestClosure(confirmation));
      setConfirmation('');
      setShowClose(false);
    } catch (err) {
      setError((err as { response?: { data?: { title?: string } } })?.response?.data?.title
        ?? t('account.closeError', 'Could not schedule the closure.'));
    } finally { setBusy(false); }
  };

  const onKeep = async () => {
    setBusy(true); setError(null);
    try {
      setClosure(await cancelClosure());
      setNotice(t('account.closeCancelled', 'Your account will stay open. Nothing has been deleted.'));
    } catch {
      setError(t('account.cancelError', 'Could not call off the closure. Please try again.'));
    } finally { setBusy(false); }
  };

  if (error && !exports) return <Alert variant="error">{error}</Alert>;
  if (!exports || !closure) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-body">{t('account.title', 'Your data and account')}</h1>

      {error && <Alert variant="error">{error}</Alert>}
      {notice && <Alert variant="success">{notice}</Alert>}

      {/* ── Scheduled closure ─────────────────────────────────────────────
          Rendered first and unmissably when active. Somebody who scheduled this in frustration, or
          by accident, should not have to go looking for the way back. */}
      {closure.isClosing && (
        <Alert variant="warning">
          <div className="space-y-3">
            <p className="font-semibold">
              {t('account.closingTitle', 'This account is scheduled to close')}
            </p>
            <p>
              {t('account.closingBody', {
                defaultValue: 'Your campaigns have stopped. Your account details and creatives will be deleted on {{date}}. Until then you can change your mind, and nothing is lost.',
                date: dateFmt(closure.dataDeletedOn, i18n.language),
              })}
            </p>
            <p className="text-sm">
              {t('account.closingExport', 'Download your data before that date — exports are deleted along with everything else.')}
            </p>
            <Button onClick={onKeep} disabled={busy}>
              {t('account.keepAccount', 'Keep my account open')}
            </Button>
          </div>
        </Alert>
      )}

      {/* ── Export ────────────────────────────────────────────────────────── */}
      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="font-semibold text-body">{t('account.exportTitle', 'Download your data')}</h2>
            <p className="mt-1 text-sm text-muted">
              {t('account.exportBody', 'A ZIP archive of your account, campaigns, creatives and wallet history. You can ask for one at any time, whether or not you are leaving.')}
            </p>
          </div>

          {/* ⚠️ Said out loud rather than discovered. An advertiser who expected a list of everyone
              who saw their advert should learn why it is not there from us, not from support. */}
          <p className="rounded-lg bg-canvas px-3 py-2 text-sm text-muted">
            {t('account.exportPrivacy', 'Impressions and clicks are included as daily totals, not as individual records. Who saw your advert is personal data belonging to that clinic or patient, and is not ours to hand over.')}
          </p>

          <Button onClick={onExport} disabled={busy}>
            {t('account.exportStart', 'Prepare a new export')}
          </Button>

          {exports.length === 0 ? (
            <p className="text-sm text-subtle-ink">{t('account.exportNone', 'You have not exported anything yet.')}</p>
          ) : (
            <ul className="divide-y divide-line-soft">
              {exports.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusTone[e.status] ?? 'bg-canvas text-muted'}`}>
                    {t(`account.status.${e.status.toLowerCase()}`, e.status)}
                  </span>
                  <span className="text-muted">{dateFmt(e.requestedAt, i18n.language)}</span>
                  {e.sizeBytes ? (
                    <span className="text-subtle-ink">{Math.max(1, Math.round(e.sizeBytes / 1024))} KB</span>
                  ) : null}
                  {e.status === 'Ready' && (
                    <Button variant="secondary" className="ml-auto" onClick={() => onDownload(e.id)}>
                      {t('account.download', 'Download')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* ── Closing ───────────────────────────────────────────────────────── */}
      {!closure.isClosing && (
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="font-semibold text-body">{t('account.closeTitle', 'Close this account')}</h2>
              <p className="mt-1 text-sm text-muted">
                {t('account.closeBody', {
                  defaultValue: 'Your campaigns stop straight away. Your account details and creatives are deleted {{days}} days later, and until then you can change your mind.',
                  days: closure.graceDays,
                })}
              </p>
            </div>

            {/* ⚠️ What survives, stated before the button rather than in terms nobody reads. */}
            <div className="rounded-lg bg-canvas px-3 py-2.5 text-sm text-muted">
              <p className="font-medium text-body-soft">{t('account.keptTitle', 'What we have to keep')}</p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5">
                <li>{t('account.keptBilling', 'Invoices we issued you, and the wallet transactions behind them — required by tax law.')}</li>
                <li>{t('account.keptEvidence', 'Impression and click totals, because they are the evidence for what you were charged. Your campaign and creative names are removed from them.')}</li>
              </ul>
            </div>

            {!showClose ? (
              <Button variant="danger" onClick={() => setShowClose(true)}>
                {t('account.closeStart', 'Close my account')}
              </Button>
            ) : (
              <form onSubmit={onClose} className="space-y-3">
                <Input
                  label={t('account.confirmLabel', {
                    defaultValue: 'Type your company name — {{name}} — to confirm',
                    name: companyName ?? '',
                  })}
                  value={confirmation}
                  onChange={(ev) => setConfirmation(ev.target.value)}
                  autoComplete="off"
                />
                <div className="flex gap-2">
                  {/* Disabled until it matches, so the destructive button is not merely guarded but
                      visibly inert. The server checks this too — this is the courtesy, not the rule. */}
                  <Button type="submit" variant="danger" disabled={busy || confirmation.trim() !== (companyName ?? '')}>
                    {t('account.closeConfirm', 'Close my account')}
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => { setShowClose(false); setConfirmation(''); }}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AccountPage;
